/**
 * Billing API service for plan management and feature gating
 */

import { API } from './apiService';

/**
 * Get the current organization's plan details
 * @returns {Promise<Object>} Plan details including tier, limits, and features
 */
export const getCurrentPlan = async () => {
  const response = await API.get('/billing/plan');
  return response;
};

/**
 * Get current usage statistics
 * @returns {Promise<Object>} Usage summary with limits and percentages
 */
export const getUsage = async () => {
  const response = await API.get('/billing/usage');
  return response;
};

/**
 * Get trial state for the current organization. Returns shape:
 *   { is_trial, days_remaining, expires_at, started_at, converted_at, was_in_trial }
 * @returns {Promise<Object>} Trial status payload
 */
export const getTrialStatus = async () => {
  const response = await API.get('/billing/trial-status');
  return response;
};

/**
 * Get all available plans for upgrade UI
 * @returns {Promise<Object>} List of plans with pricing and features
 */
export const getAvailablePlans = async () => {
  const response = await API.get('/billing/plans');
  return response;
};

/**
 * Request an upgrade to a new plan
 * @param {string} targetTier - The target plan tier
 * @param {string} billingPeriod - 'monthly' or 'annual'
 * @returns {Promise<Object>} Upgrade result (checkout URL or sales contact info)
 */
export const requestUpgrade = async (targetTier, billingPeriod = 'monthly') => {
  const response = await API.post('/billing/upgrade', {
    target_tier: targetTier,
    billing_period: billingPeriod,
  });
  return response;
};

/**
 * Check if a specific feature is enabled
 * @param {string} featureName - The feature to check (e.g., 'dbt_enabled')
 * @returns {Promise<Object>} Feature availability and upgrade info
 */
export const checkFeature = async (featureName) => {
  const response = await API.get(`/billing/check-feature/${featureName}`);
  return response;
};

/**
 * Check a specific limit
 * @param {string} limitName - The limit to check (e.g., 'max_connections')
 * @returns {Promise<Object>} Limit status and upgrade info
 */
export const checkLimit = async (limitName) => {
  const response = await API.get(`/billing/check-limit/${limitName}`);
  return response;
};

// Export all functions as a service object
export const billingService = {
  getCurrentPlan,
  getUsage,
  getTrialStatus,
  getAvailablePlans,
  requestUpgrade,
  checkFeature,
  checkLimit,
};

export default billingService;
